import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { useResolvedConfig } from "@/context/ResolvedConfigContext";
import { SupportRequestEntry } from "@/components/SupportRequestEntry";
import {
    canAccessWorkforce, WORKFORCE_ADMIN_ROLES,
} from "@/components/workforce/WorkforceRouteGuard";
import {
    LayoutGrid, CalendarDays, ChefHat, Boxes, Truck, Building2,
    ClipboardCheck, BarChart3, Megaphone, Settings as Cog, X,
    Utensils, Activity, LifeBuoy, TrendingUp, ChevronDown, UsersRound,
} from "lucide-react";

// Sidebar order — Launch Fix §2:
// Production Review must appear directly below Weekly Menu, above Recipes.
// The remaining items keep their existing relative order.
// Prompt 10A — Workforce inserted after Workflow, before Weekly Menu.
// Visibility is additionally gated by role + resolved modules.workforce
// + active restaurantId (see `filterNavForUser` below).
const NAV = [
    { to: "/workflow", label: "Workflow", icon: LayoutGrid, testId: "nav-workflow" },
    { to: "/workforce", label: "Workforce", icon: UsersRound, testId: "nav-workforce", workforce: true },
    { to: "/weekly-menu", label: "Weekly Menu", icon: CalendarDays, testId: "nav-weekly-menu" },
    { to: "/production-review", label: "Daily Performance", icon: Utensils, testId: "nav-production-review" },
    { to: "/recipes", label: "Recipes", icon: ChefHat, testId: "nav-recipes" },
    { to: "/inventory", label: "Inventory", icon: Boxes, testId: "nav-inventory" },
    { to: "/deliveries", label: "Deliveries", icon: Truck, testId: "nav-deliveries" },
    { to: "/suppliers", label: "Suppliers", icon: Building2, testId: "nav-suppliers" },
    { to: "/daily-checks", label: "Self Monitoring", icon: ClipboardCheck, testId: "nav-daily-checks" },
    { to: "/reports", label: "Reports", icon: BarChart3, testId: "nav-reports" },
    { to: "/announcements", label: "Announcements", icon: Megaphone, testId: "nav-announcements" },
    { to: "/settings", label: "Settings", icon: Cog, testId: "nav-settings" },
];

// ─────────────────────────────────────────────────────────────
// Super-admin left navigation. The full platform admin lives in
// the left sidebar; the horizontal tab bar inside AdminPortal is
// hidden for super_admin. Nested items sit under Support and
// Commercial. Alerts and Go-Live are intentionally NOT top-level.
// ─────────────────────────────────────────────────────────────
const ADMIN_NAV = [
    { kind: "single", tab: "dashboard", label: "Dashboard",  icon: LayoutGrid, testId: "nav-admin-dashboard" },
    { kind: "single", tab: "companies", label: "Companies",  icon: Building2, testId: "nav-admin-companies" },
    { kind: "single", tab: "lifecycle", label: "Lifecycle",  icon: Activity, testId: "nav-admin-lifecycle" },
    // Prompt 10A — Super Admin Workforce entry. Uses route navigation
    // rather than the `?tab=` pattern because Workforce lives at its
    // own top-level route. Visibility gated below by
    // canAccessWorkforce().
    { kind: "single", to: "/workforce", label: "Workforce",  icon: UsersRound,
      testId: "nav-admin-workforce", workforce: true },
    { kind: "group",  key: "support",   label: "Support",    icon: LifeBuoy, testId: "nav-admin-support",
      children: [
        { tab: "support",  label: "Support Tickets",   testId: "nav-admin-support-tickets" },
        { tab: "features", label: "Feature Requests",  testId: "nav-admin-features" },
        { tab: "bugs",     label: "Bug Reports",       testId: "nav-admin-bugs" },
        { tab: "training", label: "Training",          testId: "nav-admin-training" },
      ]},
    { kind: "group",  key: "commercial", label: "Commercial", icon: TrendingUp, testId: "nav-admin-commercial",
      children: [
        { tab: "commercial",   label: "Sales Overview", testId: "nav-admin-sales" },
        { tab: "plans",        label: "Plans",          testId: "nav-admin-plans" },
        { tab: "subscriptions",label: "Subscriptions",  testId: "nav-admin-subscriptions" },
        { tab: "invoices",     label: "Invoices",       testId: "nav-admin-invoices" },
        { tab: "billing_settings", label: "Billing Settings", testId: "nav-admin-billing-settings" },
      ]},
    { kind: "single", tab: "reports",  label: "Reports",  icon: BarChart3, testId: "nav-admin-reports" },
    { kind: "single", tab: "settings", label: "Settings", icon: Cog,       testId: "nav-admin-settings" },
];

// Prompt 10A helper — hide Workforce entries unless the user + config
// + active restaurant pass the shared UX guard. Backend is authoritative.
function filterNavForUser(items, ctx) {
    return items.filter((item) => {
        if (!item.workforce) return true;
        return canAccessWorkforce(ctx);
    });
}

// Keep the selected item near the top of the scrollable menu (small gap above it).
function useScrollActiveNavItem() {
    const ref = useRef(null);
    const location = useLocation();
    useEffect(() => {
        const nav = ref.current;
        const el = nav?.querySelector(".kop-nav-item.active");
        if (!el || nav.scrollHeight <= nav.clientHeight) return;
        const top = el.getBoundingClientRect().top - nav.getBoundingClientRect().top + nav.scrollTop - 48;
        nav.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }, [location.pathname, location.search]);
    return ref;
}

function AdminNavItems({ onClick }) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const activeTab = params.get("tab") || "dashboard";
    const { t } = useTranslation();
    const { user, restaurantId } = useApp();
    const { config } = useResolvedConfig();
    const items = filterNavForUser(ADMIN_NAV,
                                        { user, restaurantId, config });

    // Auto-open the group that contains the active tab.
    const initialOpen = () => {
        const g = items.find((i) =>
            i.kind === "group" && i.children.some((c) => c.tab === activeTab));
        return g ? { [g.key]: true } : {};
    };
    const [open, setOpen] = useState(initialOpen);
    const navRef = useScrollActiveNavItem();

    const go = (tab) => {
        navigate(`/admin?tab=${tab}`);
        onClick?.();
    };

    return (
        <nav ref={navRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1" data-testid="admin-sidebar-nav">
            {items.map((item) => {
                if (item.kind === "single") {
                    // Prompt 10A — a `single` item may declare either a
                    // legacy `tab` (Admin Portal sub-tab) OR a `to`
                    // route path. Route items use react-router
                    // NavLink; tab items keep the historical
                    // `?tab=` navigation. Behavior for existing tab
                    // items is unchanged.
                    if (item.to) {
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={item.testId}
                                onClick={onClick}
                                className={({ isActive }) =>
                                    `kop-nav-item ${isActive ? "active" : ""}`}
                            >
                                <item.icon className="h-4 w-4 shrink-0"
                                              strokeWidth={2} />
                                <span className="text-sm">{t(`nav.${item.testId}`, item.label)}</span>
                            </NavLink>
                        );
                    }
                    const isActive = location.pathname.startsWith("/admin") && activeTab === item.tab;
                    return (
                        <button
                            key={item.tab}
                            data-testid={item.testId}
                            onClick={() => go(item.tab)}
                            className={`kop-nav-item w-full text-left ${isActive ? "active" : ""}`}
                        >
                            <item.icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                            <span className="text-sm">{t(`nav.${item.testId}`, item.label)}</span>
                        </button>
                    );
                }
                // group
                const hasActive = item.children.some((c) => c.tab === activeTab);
                const isOpen = !!open[item.key] || hasActive;
                return (
                    <div key={item.key}>
                        <button
                            data-testid={item.testId}
                            onClick={() => setOpen((s) => ({ ...s, [item.key]: !isOpen }))}
                            className={`kop-nav-item w-full text-left ${hasActive ? "active" : ""}`}
                        >
                            <item.icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                            <span className="text-sm flex-1">{t(`nav.${item.testId}`, item.label)}</span>
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        </button>
                        {isOpen && (
                            <div className="ml-6 mt-1 space-y-1 border-l border-neutral-200 pl-3">
                                {item.children.map((c) => {
                                    const isActive = activeTab === c.tab;
                                    return (
                                        <button
                                            key={c.tab}
                                            data-testid={c.testId}
                                            onClick={() => go(c.tab)}
                                            className={`w-full text-left text-sm px-2.5 py-1.5 rounded-md ${
                                                isActive
                                                    ? "bg-neutral-900 text-white font-semibold"
                                                    : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
                                            }`}
                                        >
                                            {t(`nav.${c.testId}`, c.label)}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </nav>
    );
}

function KitchenNavItems({ onClick }) {
    const { t } = useTranslation();
    const { user, restaurantId } = useApp();
    const { config } = useResolvedConfig();
    const items = filterNavForUser(NAV, { user, restaurantId, config });
    const navRef = useScrollActiveNavItem();
    return (
        <nav ref={navRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1" data-testid={onClick ? "kitchen-sidebar-nav-mobile" : "kitchen-sidebar-nav"}>
            {items.map((item) => (
                <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={item.testId}
                    onClick={onClick}
                    className={({ isActive }) => `kop-nav-item ${isActive ? "active" : ""}`}
                >
                    <item.icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                    <span className="text-sm">{t(`nav.${item.testId}`, item.label)}</span>
                </NavLink>
            ))}
        </nav>
    );
}

function NavItems({ onClick }) {
    const { user } = useApp();
    return user?.role === "super_admin"
        ? <AdminNavItems onClick={onClick} />
        : <KitchenNavItems onClick={onClick} />;
}

function Brand() {
    const { t } = useTranslation();
    const { user } = useApp();
    const isSuperAdmin = user?.role === "super_admin";
    return (
        <div className="flex items-center gap-3">
            <img
                src="/mise-one-logo.png"
                alt="MiseOne"
                className="h-9 w-9 object-contain"
            />
            <div className="leading-tight">
                <div className="font-brand text-lg font-semibold tracking-tight">MiseOne</div>
                <div data-testid="sidebar-tagline" className="text-[9px] uppercase tracking-[0.18em] text-neutral-400">
                    {isSuperAdmin ? t("brand.platformConsole", "Platform Console") : t("brand.tagline", "Less work. More cooking.")}
                </div>
            </div>
        </div>
    );
}

export default function Sidebar({ mobileOpen, onMobileClose }) {
    const { t } = useTranslation();
    useEffect(() => {
        if (mobileOpen) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => { document.body.style.overflow = prev; };
        }
    }, [mobileOpen]);

    return (
        <>
            <aside
                data-testid="sidebar"
                className="hidden lg:flex flex-col w-64 border-r border-neutral-200 bg-white lg:sticky lg:top-0 lg:h-screen lg:shrink-0"
            >
                <div className="px-5 py-5 border-b border-neutral-100">
                    <Brand />
                </div>
                <NavItems />
                <SupportRequestEntry />
            </aside>

            {mobileOpen && (
                <div
                    data-testid="sidebar-mobile-backdrop"
                    className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm animate-[ms-fade-in-up_160ms_ease-out]"
                    onClick={onMobileClose}
                />
            )}
            <aside
                data-testid="sidebar-mobile"
                className={`lg:hidden fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-xl flex flex-col transition-transform duration-200 ease-out ${
                    mobileOpen ? "translate-x-0" : "-translate-x-full"
                }`}
            >
                <div className="px-5 py-5 border-b border-neutral-100 flex items-center justify-between">
                    <Brand />
                    <button
                        data-testid="sidebar-mobile-close"
                        onClick={onMobileClose}
                        aria-label={t("nav.closeNavigation", "Close navigation")}
                        className="h-9 w-9 rounded-md hover:bg-neutral-100 grid place-items-center"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <NavItems onClick={onMobileClose} />
                <SupportRequestEntry suffix="-mobile" onOpen={onMobileClose} />
            </aside>
        </>
    );
}
